import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { DocumentAttachments } from "@/components/DocumentAttachments";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, ArrowLeft, BedDouble, Trash2, AlertTriangle, Check, X,
  ReceiptText, FileDown, Send, Pencil, CalendarDays, MoreHorizontal,
  ClipboardCheck, ExternalLink, Sparkles, LayoutPanelLeft, Eye, EyeOff, ShieldCheck, Mail, Upload, Download, Handshake, Plane, BadgeCheck, Shuffle, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { AgencyTransportDialog } from "@/components/AgencyTransportDialog";
import { VisitorCommentsDialog } from "@/components/VisitorCommentsDialog";
import { todayLocalIso, addDaysIso, parseLocalDate, toLocalIso } from "@/lib/dateUtils";

const STATUS_COLOR = {
  draft: "bg-gray-100 text-gray-700 border-gray-200",
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  checked_in: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-[#E9F5DB] text-[#1B4332] border-emerald-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

export default function Bookings() {
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [units, setUnits] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [rateCards, setRateCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [showOnlyUntagged, setShowOnlyUntagged] = useState(false);
  // Phase 65 — bulk agency tagging
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkSponsor, setBulkSponsor] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [surveyOptions, setSurveyOptions] = useState([]);
  // Phase 51.4 — display currency (KES/USD/both). Persists per user.
  const [displayCurrency, setDisplayCurrency] = useState(() => {
    try { return localStorage.getItem("bookings-display-currency") || "KES"; }
    catch { return "KES"; }
  });
  useEffect(() => {
    try { localStorage.setItem("bookings-display-currency", displayCurrency); } catch { /* ignore */ }
  }, [displayCurrency]);
  // Default hide cancelled bookings from the list — they clutter the view.
  // Persist across reloads so the coordinator's preference sticks.
  const [hideCancelled, setHideCancelled] = useState(() => {
    try { return localStorage.getItem("bookings-hide-cancelled") !== "false"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("bookings-hide-cancelled", String(hideCancelled)); } catch { /* ignore */ }
  }, [hideCancelled]);
  const [showCalendar, setShowCalendar] = useState(true);
  const [calendarGrid, setCalendarGrid] = useState({ units: [], bookings_by_unit: {} });
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [mlDefaults, setMlDefaults] = useState({ default_fee_amount: 0, default_fee_currency: "KES", default_recipients: [] });
  // Phase 51 — Excel importer state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importRows, setImportRows] = useState([]); // { ...candidate, selected, unit_id, party_size }
  const [importCommitting, setImportCommitting] = useState(false);
  // Phase 52 — Visitor Calendar sync (departments dropdown)
  const [departments, setDepartments] = useState([]);
  // Phase 58 — sending sponsor dropdown
  const [sponsorOrgs, setSponsorOrgs] = useState([]);
  useEffect(() => {
    api.get("/departments").then(({ data }) => setDepartments(data || [])).catch(() => {});
    api.get("/sponsor-orgs").then(({ data }) => setSponsorOrgs(data || [])).catch(() => setSponsorOrgs([]));
    api.get("/surveys/options").then(({ data }) => setSurveyOptions(data || [])).catch(() => {});
  }, []);

  // Phase 55 — Transport defaults applied when the user first ticks the toggle.
  const ensureTransport = (existing) => existing || {
    needed: true,
    rate_amount: 0,
    rate_currency: "KES",
    contact_phone: "",
    pickup_location: "",
    dropoff_location: "",
    notes: "",
    inbound: { date: "", time: "", flight_number: "", notes: "" },
    outbound: { date: "", time: "", flight_number: "", notes: "" },
  };
  const updateTransport = (patch) =>
    setEditing((p) => ({ ...p, transport: { ...ensureTransport(p.transport), ...patch } }));
  const updateTransportLeg = (leg, patch) =>
    setEditing((p) => {
      const t = ensureTransport(p.transport);
      return { ...p, transport: { ...t, [leg]: { ...t[leg], ...patch } } };
    });

  const canManage = ["admin", "global_admin", "hospitality_coordinator", "booking_coordinator"].includes(user.role);
  // Phase 69 — initiator/agency scoped flight & date editing
  const canAgencyTransport = !canManage && hasPerm("bookings.update_transport");
  const [transportEditing, setTransportEditing] = useState(null);
  const [agencyMargin, setAgencyMargin] = useState(2);
  const [ackingId, setAckingId] = useState(null);
  useEffect(() => {
    if (canAgencyTransport) {
      api.get("/housing/agency-booking-settings")
        .then(({ data }) => setAgencyMargin(data.date_margin_days ?? 2)).catch(() => {});
    }
  }, [canAgencyTransport]);
  // Phase 73 — returning visitor picker + comments
  const [visitorQuery, setVisitorQuery] = useState("");
  const [visitorHits, setVisitorHits] = useState([]);
  const [commentFor, setCommentFor] = useState(null);
  const searchVisitors = (q) => {
    setVisitorQuery(q);
    if (!q || q.length < 2) { setVisitorHits([]); return; }
    api.get(`/visitors?q=${encodeURIComponent(q)}&limit=8`).then(({ data }) => setVisitorHits(data || [])).catch(() => {});
  };
  const pickVisitor = async (v) => {
    setVisitorHits([]);
    setVisitorQuery(`${v.visitor_no} — ${v.name}`);
    let lic = null;
    try {
      const { data } = await api.get(`/visitors/${v.id}`);
      lic = data.license_summary;
    } catch { /* profile fetch optional */ }
    setEditing((p) => ({
      ...p,
      visitor_id: v.id, visitor_no: v.visitor_no,
      visitor_name: v.name, visitor_email: v.email || p.visitor_email,
      guest_gender: v.gender || p.guest_gender,
      ...(lic ? {
        needs_medical_license: true,
        medical_license_profession: lic.profession || p.medical_license_profession,
        medical_license_number: lic.license_number || p.medical_license_number,
        _license_pulled: true,
      } : {}),
    }));
    if (v.preferences) toast.info(`Preferences: ${v.preferences}`, { duration: 6000 });
  };

  const acknowledgeRevision = async (b) => {
    setAckingId(b.id);
    try {
      await api.post(`/bookings/${b.id}/acknowledge-revision`);
      toast.success(`Revision on ${b.visitor_name} acknowledged`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to acknowledge");
    } finally { setAckingId(null); }
  };

  const load = useCallback(async () => {
    try {
      const [b, u, t, c, rc, mls] = await Promise.all([
        api.get("/bookings"),
        api.get("/housing/units"),
        api.get("/housing/tiers"),
        api.get("/housing/cleaners"),
        api.get("/housing/rate-cards"),
        api.get("/housing/medical-license-settings").catch(() => ({ data: null })),
      ]);
      setBookings(b.data); setUnits(u.data); setTiers(t.data); setCleaners(c.data);
      setRateCards(rc.data);
      if (mls.data) setMlDefaults(mls.data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Phase 51 — Excel importer handlers
  const handleImportPreview = async () => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/bookings/import-excel/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview(data);
      // Default: select all rows that have a matched unit AND aren't likely duplicates
      const rows = (data.candidates || []).map((c, i) => ({
        ...c,
        _idx: i,
        selected: !!c.unit_id && !c.likely_duplicate,
        party_size: 1,
      }));
      setImportRows(rows);
      toast.success(`Found ${data.total} candidate booking${data.total === 1 ? "" : "s"} across ${(data.candidates || []).reduce((s, c) => s.add(c.source_sheet), new Set()).size} sheet(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not parse the workbook");
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportCommit = async () => {
    const selected = importRows.filter((r) => r.selected && r.unit_id);
    if (selected.length === 0) {
      toast.error("Select at least one row with a unit assigned");
      return;
    }
    setImportCommitting(true);
    try {
      const { data } = await api.post("/bookings/import-excel/commit", {
        rows: selected.map((r) => ({
          visitor_name: r.visitor_name,
          unit_id: r.unit_id,
          from_date: r.from_date,
          to_date: r.to_date,
          party_size: r.party_size || 1,
          notes: "",
          source_sheet: r.source_sheet || "",
          // Phase 51.1 — pass through master-list metadata + reconciliation info
          source: r.source || "grid",
          email: r.email || "",
          specialty: r.specialty || "",
          transport_cost: r.transport_cost ?? null,
          medical_licenses_paid: !!r.medical_licenses_paid,
          warnings: r.warnings || [],
        })),
      });
      toast.success(
        `Imported ${data.created.length} draft booking${data.created.length === 1 ? "" : "s"}` +
        (data.skipped.length ? ` · ${data.skipped.length} skipped` : "") +
        (data.errors.length ? ` · ${data.errors.length} error${data.errors.length === 1 ? "" : "s"}` : "")
      );
      setImportOpen(false);
      setImportFile(null);
      setImportPreview(null);
      setImportRows([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImportCommitting(false);
    }
  };

  const updateImportRow = (idx, patch) => {
    setImportRows((prev) => prev.map((r) => (r._idx === idx ? { ...r, ...patch } : r)));
  };

  const toggleAllImportRows = (checked) => {
    setImportRows((prev) => prev.map((r) => ({ ...r, selected: checked && !!r.unit_id })));
  };

  // Phase 51.2 — Sync back to Excel: downloads a cleaned workbook mirroring the
  // original Kapsowar format (Master List + monthly calendar grids + Legend).
  const handleExportExcel = async () => {
    try {
      toast.loading("Building workbook…", { id: "export-xlsx" });
      const today = new Date();
      const oneYearOut = new Date(today.getFullYear() + 1, today.getMonth(), Math.min(today.getDate(), 28));
      const fmt = (d) => d.toISOString().slice(0, 10);
      const res = await api.get(
        `/bookings/export-excel?date_from=${fmt(today)}&date_to=${fmt(oneYearOut)}`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kapsowar-hospitality-${fmt(today)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded — check your browser's Downloads folder", { id: "export-xlsx" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed", { id: "export-xlsx" });
    }
  };

  // When the coordinator clicks "Finalise booking" on a request detail page,
  // we receive location.state.openBookingId and auto-open that booking.
  // Phase 56 — also support `?edit=ID` query param (used by the Visitor Calendar
  // and other deep-links). We remember the `from` path on location.state so
  // closing the dialog returns there instead of staying on /bookings.
  const [returnTo, setReturnTo] = useState(null);
  useEffect(() => {
    if (location.state?.from) setReturnTo(location.state.from);
  }, [location.state?.from]);

  useEffect(() => {
    const openId = location.state?.openBookingId
      || new URLSearchParams(location.search || "").get("edit");
    if (!openId || bookings.length === 0) return;
    const b = bookings.find((x) => x.id === openId);
    if (b) {
      setEditing({
        ...b,
        segments: b.segments.map((s) => ({ ...s })),
        add_ons: (b.add_ons || []).map((a) => ({ ...a })),
      });
      // Strip the query/state so the dialog doesn't re-open on back-nav
      navigate(location.pathname, { replace: true, state: returnTo ? { from: returnTo } : {} });
    }
  }, [bookings, location.state, location.search, location.pathname, navigate, returnTo]);

  const pendingCount = useMemo(
    () => bookings.filter((b) => b.pending_housing_approval && b.status === "draft").length,
    [bookings],
  );

  // Progress tabs (All · Draft · Confirmed · Checked in · Checked out · Cleared · Cancelled)
  const PROGRESS_TABS = [
    { k: "all", label: "All" },
    { k: "draft", label: "Draft" },
    { k: "confirmed", label: "Confirmed" },
    { k: "checked_in", label: "Checked in" },
    { k: "checked_out", label: "Checked out" },
    { k: "cleared", label: "Cleared" },
    { k: "cancelled", label: "Cancelled" },
  ];
  const [progressTab, setProgressTab] = useState(() => {
    try { return localStorage.getItem("bookings-default-tab") || "all"; } catch { return "all"; }
  });
  const progressCounts = useMemo(() => {
    const c = { all: bookings.length };
    for (const b of bookings) {
      const k = b.progress_state || b.status || "draft";
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [bookings]);
  const setDefaultTab = () => {
    try { localStorage.setItem("bookings-default-tab", progressTab); } catch { /* ignore */ }
    toast.success(`"${PROGRESS_TABS.find((t) => t.k === progressTab)?.label}" is now your default view`);
  };

  // Sort: pending-housing drafts first, then by stay date.
  const sortedBookings = useMemo(() => {
    let copy = [...bookings];
    if (progressTab !== "all") {
      copy = copy.filter((b) => (b.progress_state || b.status) === progressTab);
    } else if (hideCancelled) {
      copy = copy.filter((b) => b.status !== "cancelled");
    }
    copy.sort((a, b) => {
      const aP = a.pending_housing_approval && a.status === "draft" ? 0 : 1;
      const bP = b.pending_housing_approval && b.status === "draft" ? 0 : 1;
      if (aP !== bP) return aP - bP;
      return (a.stay_from || "").localeCompare(b.stay_from || "");
    });
    if (showOnlyUntagged) {
      copy = copy.filter((b) => !b.sponsor_org_id && b.status !== "cancelled");
    }
    return showOnlyPending
      ? copy.filter((b) => b.pending_housing_approval && b.status === "draft")
      : copy;
  }, [bookings, showOnlyPending, showOnlyUntagged, hideCancelled, progressTab]);

  const untaggedCount = useMemo(
    () => bookings.filter((b) => !b.sponsor_org_id && b.status !== "cancelled").length,
    [bookings],
  );

  const sponsorMap = useMemo(() => {
    const m = {};
    sponsorOrgs.forEach((s) => { m[s.id] = s.name; });
    return m;
  }, [sponsorOrgs]);

  const toggleSelect = (id) => setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const allVisibleSelected = sortedBookings.length > 0 && sortedBookings.every((b) => selectedIds.includes(b.id));
  const toggleSelectAll = () => setSelectedIds(allVisibleSelected ? [] : sortedBookings.map((b) => b.id));
  const exitBulkMode = () => { setBulkMode(false); setSelectedIds([]); setBulkSponsor(""); };

  const applyBulk = async () => {
    if (!bulkSponsor) return toast.error("Pick an agency first");
    if (selectedIds.length === 0) return toast.error("Select at least one booking");
    setBulkSaving(true);
    try {
      const { data } = await api.post("/bookings/bulk-assign-sponsor", {
        booking_ids: selectedIds,
        sponsor_org_id: bulkSponsor,
      });
      toast.success(`Tagged ${data.updated} booking${data.updated === 1 ? "" : "s"} to ${data.sponsor_name}`);
      exitBulkMode();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk tagging failed"); }
    finally { setBulkSaving(false); }
  };

  const cancelledCount = useMemo(
    () => bookings.filter((b) => b.status === "cancelled").length,
    [bookings],
  );

  const unitMap = useMemo(() => Object.fromEntries(units.map(u => [u.id, u])), [units]);
  const tierMap = useMemo(() => Object.fromEntries(tiers.map(t => [t.id, t])), [tiers]);

  const newBooking = () => setEditing({
    visitor_name: "",
    visitor_email: "",
    party_size: 1,
    guest_gender: "",
    stay_from: todayLocalIso(),
    stay_to: addDaysIso(todayLocalIso(), 7),
    segments: [],
    add_ons: [],
    notes: "",
    status: "confirmed",
    add_to_visitor_calendar: false,
    all_dept_approvals_confirmed: false,
    visitor_specialty: "",
    visitor_department: "",
    visitor_level: "",
  });

  const addSegment = () => {
    if (!editing) return;
    const firstUnit = units.find(u => u.active) || units[0];
    const firstActiveCard = rateCards.find(rc => rc.active);
    setEditing({
      ...editing,
      segments: [
        ...editing.segments,
        {
          unit_id: firstUnit?.id || "",
          from_date: editing.stay_from,
          to_date: editing.stay_to,
          occupancy: editing.party_size || 1,
          rate_card_id: firstActiveCard?.id || null,
          rate_card_name: firstActiveCard?.name || "",
          rate_per_night: firstActiveCard?.amount || 0,
          currency: firstActiveCard?.currency || "KES",
          cleaner_id: null,
          notes: "",
        },
      ],
    });
  };

  const updateSegment = (idx, patch) => {
    const segs = [...editing.segments];
    segs[idx] = { ...segs[idx], ...patch };
    // When a rate card is picked, overwrite rate_per_night, currency and snapshot the name.
    if (Object.prototype.hasOwnProperty.call(patch, "rate_card_id")) {
      const rc = rateCards.find(r => r.id === patch.rate_card_id);
      if (rc) {
        segs[idx].rate_card_name = rc.name;
        segs[idx].rate_per_night = rc.amount;
        segs[idx].currency = rc.currency;
      } else {
        segs[idx].rate_card_name = "";
      }
    }
    setEditing({ ...editing, segments: segs });
  };

  const removeSegment = (idx) => {
    const segs = [...editing.segments]; segs.splice(idx, 1);
    setEditing({ ...editing, segments: segs });
  };

  const suggestSegments = async () => {
    if (!editing.stay_from || !editing.stay_to) {
      toast.error("Set arrival and departure dates first");
      return;
    }
    if (editing.segments.length > 0) {
      const ok = window.confirm(
        `This will replace your ${editing.segments.length} current segment${editing.segments.length === 1 ? "" : "s"} with inventory-based suggestions. Continue?`,
      );
      if (!ok) return;
    }
    try {
      const { data } = await api.post("/bookings/suggest-segments", {
        stay_from: editing.stay_from,
        stay_to: editing.stay_to,
        party_size: editing.party_size || 1,
        exclude_booking_id: editing.id || null,
      });
      const newSegs = (data.segments || []).map((s) => ({
        unit_id: s.unit_id,
        from_date: s.from_date,
        to_date: s.to_date,
        occupancy: s.occupancy,
        rate_card_id: s.rate_card_id,
        rate_card_name: s.rate_card_name,
        rate_per_night: s.rate_per_night,
        currency: s.currency,
        cleaner_id: null,
        notes: "",
      }));
      setEditing({ ...editing, segments: newSegs });
      if (newSegs.length === 0) {
        toast.error("No free housing units found in this window. Check availability or pick manually.");
      } else if ((data.uncovered_nights || []).length > 0) {
        toast.warning(
          `Suggested ${newSegs.length} segment${newSegs.length === 1 ? "" : "s"} but ${data.uncovered_nights.length} night${data.uncovered_nights.length === 1 ? " has" : "s have"} no unit free.`,
          { duration: 5000 },
        );
      } else {
        toast.success(
          `Suggested ${newSegs.length} segment${newSegs.length === 1 ? "" : "s"} covering all ${data.total_nights} night${data.total_nights === 1 ? "" : "s"}. Review and adjust.`,
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate suggestions");
    }
  };

  const addAddOn = () => setEditing({
    ...editing,
    add_ons: [...editing.add_ons, { label: "", quantity: 1, unit_price: 0, currency: "KES" }],
  });
  const updateAddOn = (idx, patch) => {
    const arr = [...editing.add_ons]; arr[idx] = { ...arr[idx], ...patch };
    setEditing({ ...editing, add_ons: arr });
  };
  const removeAddOn = (idx) => {
    const arr = [...editing.add_ons]; arr.splice(idx, 1);
    setEditing({ ...editing, add_ons: arr });
  };

  // Live availability/coverage check
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!editing || !editing.segments.length) { setCoverage(null); setConflict(null); return; }
      try {
        const { data } = await api.post("/bookings/check-availability", {
          ...editing,
          status: editing.status,
          booking_id: editing.id || null,  // exclude self when editing
          allow_override: !!editing.allow_override,
        });
        if (!data.ok) {
          setConflict({ detail: data.detail, blocking: true });
          setCoverage(null);
        } else if (data.override_conflict) {
          // Override active — show a warning but allow save.
          setConflict({ detail: data.override_conflict, blocking: false });
          setCoverage(data.uncovered_nights || []);
        } else {
          setConflict(null);
          setCoverage(data.uncovered_nights || []);
        }
      } catch (e) {
        setConflict({ detail: e.response?.data?.detail || "Conflict detected", blocking: true });
        setCoverage(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [editing]);

  const totals = useMemo(() => {
    if (!editing) return {};
    const t = { KES: 0, USD: 0 };
    for (const s of editing.segments) {
      const nights = Math.max(0, (parseLocalDate(s.to_date) - parseLocalDate(s.from_date)) / 86400000);
      const cur = (s.currency || "KES").toUpperCase();
      t[cur] = (t[cur] || 0) + nights * Number(s.rate_per_night || 0);
    }
    for (const a of editing.add_ons) {
      const cur = (a.currency || "KES").toUpperCase();
      t[cur] = (t[cur] || 0) + Number(a.quantity || 1) * Number(a.unit_price || 0);
    }
    if (editing.needs_medical_license && Number(editing.medical_license_fee_amount || 0) > 0) {
      const mlCur = (editing.medical_license_fee_currency || "KES").toUpperCase();
      t[mlCur] = (t[mlCur] || 0) + Number(editing.medical_license_fee_amount || 0);
    }
    // Phase 51.4 — Transport charge included in the running total.
    const tr = editing.transport || {};
    if (tr.needed && Number(tr.rate_amount || 0) > 0) {
      const trCur = (tr.rate_currency || "KES").toUpperCase();
      t[trCur] = (t[trCur] || 0) + Number(tr.rate_amount || 0);
    }
    return t;
  }, [editing]);

  // Phase 51.4 — Convert to a single-currency figure using the org's FX rate.
  const [orgFxRate, setOrgFxRate] = useState(130);
  useEffect(() => {
    api.get("/housing/exchange-rate").then(({ data }) => {
      if (data?.usd_to_kes) setOrgFxRate(Number(data.usd_to_kes));
    }).catch(() => {});
  }, []);

  // Phase 51.5 — Email quick-reference: reuse any address previously typed on
  // bookings / leases / requests / licence recipients / staff / vendors etc.
  // Refetched whenever the editing dialog is opened (or a fresh save happens)
  // so newly-added addresses become suggestible immediately.
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const reloadEmailSuggestions = useCallback(async () => {
    try {
      const { data } = await api.get("/housing/contacts/emails");
      setEmailSuggestions(data || []);
    } catch { /* suggestion list is optional */ }
  }, []);
  useEffect(() => { reloadEmailSuggestions(); }, [reloadEmailSuggestions]);
  // Re-fetch suggestions when a NEW booking is opened. We track the last
  // fetched id in a ref so we only trigger once per open (not on every render).
  const lastFetchedEditingIdRef = useRef(null);
  useEffect(() => {
    if (editing && editing.id !== lastFetchedEditingIdRef.current) {
      lastFetchedEditingIdRef.current = editing.id;
      reloadEmailSuggestions();
    }
  }, [editing, reloadEmailSuggestions]);
  const convertedTotals = useMemo(() => {
    const kes = Number(totals.KES || 0);
    const usd = Number(totals.USD || 0);
    const rate = orgFxRate || 130;
    return {
      inKes: kes + usd * rate,
      inUsd: usd + (kes / rate),
    };
  }, [totals, orgFxRate]);

  // Phase 52 — when opening an existing booking with a linked request, prefill
  // the visitor-calendar sync fields so the panel reflects the live state.
  // Only auto-check the toggle when the linked request was originally created
  // via this booking sync (otherwise the standard approval flow owns it).
  useEffect(() => {
    if (!editing?.id || !editing?.request_id) return;
    if (editing._vc_prefilled) return;
    let cancelled = false;
    api.get(`/requests/${editing.request_id}`).then(({ data }) => {
      if (cancelled || !data) return;
      const lvl = data.is_student ? "student" : data.is_resident ? "resident" : "faculty";
      const ownsSync = !!data.synced_from_booking;
      setEditing((prev) => prev && prev.id === editing.id ? {
        ...prev,
        add_to_visitor_calendar: ownsSync || !!prev.add_to_visitor_calendar,
        all_dept_approvals_confirmed: ownsSync || !!prev.all_dept_approvals_confirmed,
        visitor_specialty: data.specialty || prev.visitor_specialty || "",
        visitor_department: data.department || prev.visitor_department || "",
        visitor_level: lvl,
        _vc_prefilled: true,
      } : prev);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [editing?.id, editing?.request_id, editing?._vc_prefilled]);

  const saveBooking = async () => {
    if (!editing.visitor_name.trim()) { toast.error("Visitor name required"); return; }
    if (editing.segments.length === 0) { toast.error("Add at least one stay segment"); return; }
    // Phase 52 — visitor calendar sync validation
    if (editing.add_to_visitor_calendar) {
      if (!editing.all_dept_approvals_confirmed) {
        toast.error("Confirm all department approvals before adding to the Visitor Calendar");
        return;
      }
      if (!(editing.visitor_specialty || "").trim() || !(editing.visitor_department || "").trim()) {
        toast.error("Specialty and department are required for Visitor Calendar entries");
        return;
      }
      if (!editing.visitor_level) {
        toast.error("Pick a level (Student / Resident / Faculty)");
        return;
      }
    }
    try {
      let result;
      if (editing.id) {
        const { data } = await api.put(`/bookings/${editing.id}`, editing);
        result = data;
      } else {
        const { data } = await api.post("/bookings", editing);
        result = data;
      }
      if (result?.auto_approved_request) {
        toast.success("Housing confirmed — request auto-approved & visitor notified", { duration: 4500 });
      } else if (result?.shared_room) {
        toast.success("Booking saved with room-share override", { duration: 4000 });
      } else {
        toast.success("Booking saved");
      }
      setEditing(null);
      load();
      // Phase 56 — return to the page that opened this dialog (e.g. /calendar)
      if (returnTo) {
        const dest = returnTo;
        setReturnTo(null);
        navigate(dest);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  // Phase 56 — explicit close handler: also returns to the originating page.
  const closeBookingDialog = () => {
    setEditing(null);
    if (returnTo) {
      const dest = returnTo;
      setReturnTo(null);
      navigate(dest);
    }
  };

  // Phase 56 — delete a booking (and its linked Visitor Calendar request,
  // when applicable) directly from the edit dialog.
  const deleteBookingFromDialog = async () => {
    if (!editing?.id) return;
    if (!window.confirm(`Delete booking for ${editing.visitor_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/bookings/${editing.id}`);
      // Soft-delete linked visitor request too so the calendar entry disappears
      if (editing.request_id) {
        try {
          await api.post("/requests/bulk-soft-delete", { request_ids: [editing.request_id] });
        } catch { /* best-effort */ }
      }
      toast.success("Booking deleted");
      setEditing(null);
      load();
      if (returnTo) {
        const dest = returnTo;
        setReturnTo(null);
        navigate(dest);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Mini calendar window: stay window ± 3 days (expanded to include any
  // segment that sits outside stay_from/stay_to). Re-fetches when the dialog
  // opens and whenever the dates shift.
  const calendarWindow = useMemo(() => {
    if (!editing) return null;
    const dates = [editing.stay_from, editing.stay_to, ...(editing.segments || []).flatMap((s) => [s.from_date, s.to_date])]
      .filter(Boolean);
    if (dates.length < 2) return null;
    const min = new Date(Math.min(...dates.map((d) => parseLocalDate(d).getTime())));
    const max = new Date(Math.max(...dates.map((d) => parseLocalDate(d).getTime())));
    min.setDate(min.getDate() - 3);
    max.setDate(max.getDate() + 3);
    return { from: toLocalIso(min), to: toLocalIso(max) };
  }, [editing]);

  useEffect(() => {
    if (!editing || !showCalendar || !calendarWindow) return;
    let cancelled = false;
    setCalendarLoading(true);
    (async () => {
      try {
        const { data } = await api.get(
          `/bookings/calendar/grid?from_date=${calendarWindow.from}&to_date=${calendarWindow.to}`,
        );
        if (!cancelled) setCalendarGrid(data);
      } catch {
        if (!cancelled) setCalendarGrid({ units: [], bookings_by_unit: {} });
      } finally {
        if (!cancelled) setCalendarLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editing, calendarWindow, showCalendar]);

  const calendarDays = useMemo(() => {
    if (!calendarWindow) return [];
    const out = [];
    const cur = parseLocalDate(calendarWindow.from);
    const end = parseLocalDate(calendarWindow.to);
    while (cur <= end) { out.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    return out;
  }, [calendarWindow]);

  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel booking for ${b.visitor_name}?`)) return;
    try { await api.delete(`/bookings/${b.id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const generateInvoice = async (b) => {
    try {
      const { data } = await api.post(`/bookings/${b.id}/invoice`);
      if (data.was_regenerated) {
        toast.success(`Invoice #${data.invoice_number} refreshed from booking`, { duration: 4000 });
      } else {
        toast.success(`Invoice #${data.invoice_number} generated`);
      }
      navigate("/invoices");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="bookings-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Bookings</h1>
          <p className="text-[#525C58] mt-1">Multi-segment housing assignments with conflict & coverage checks.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/housing-calendar")} className="border-[#1B4332]/20 text-[#1B4332]" data-testid="open-housing-calendar-btn">
            <CalendarDays className="h-4 w-4 mr-2" /> Housing calendar
          </Button>
          {canManage && (
            <Button variant="outline" onClick={() => navigate("/scramble")} className="border-amber-400 text-amber-800 hover:bg-amber-50" data-testid="scramble-btn">
              <Shuffle className="h-4 w-4 mr-2" /> Scramble
            </Button>
          )}
          {canManage && (
            <Button
              variant="outline"
              onClick={() => setImportOpen(true)}
              className="border-[#1B4332]/20 text-[#1B4332]"
              data-testid="import-excel-btn"
            >
              <Upload className="h-4 w-4 mr-2" /> Import Excel
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleExportExcel}
            className="border-[#1B4332]/20 text-[#1B4332]"
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Export Excel
          </Button>
          {canManage && (
            <Button onClick={newBooking} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="new-booking-btn">
              <Plus className="h-4 w-4 mr-2" /> New booking
            </Button>
          )}
        </div>
      </div>

      {/* Progress tabs */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="booking-progress-tabs">
        {PROGRESS_TABS.map((t) => (
          <button key={t.k} onClick={() => setProgressTab(t.k)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                    progressTab === t.k
                      ? "bg-[#1B4332] text-white border-[#1B4332]"
                      : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
                  }`}
                  data-testid={`progress-tab-${t.k}`}>
            {t.label}
            <span className={`ml-1.5 tabular-nums ${progressTab === t.k ? "text-white/70" : "text-[#A3ACA8]"}`}>
              {progressCounts[t.k] || 0}
            </span>
          </button>
        ))}
        <button onClick={setDefaultTab}
                className="text-[11px] text-[#7A8A84] hover:text-[#1B4332] underline decoration-dotted ml-1"
                title="Open this tab by default next time"
                data-testid="set-default-tab">
          Set as my default
        </button>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2 text-lg flex-wrap">
            <span className="flex items-center gap-2">
              <BedDouble className="h-5 w-5 text-[#40916C]" /> All bookings ({sortedBookings.length}{sortedBookings.length !== bookings.length ? ` of ${bookings.length}` : ""})
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-1.5 text-xs font-normal text-[#525C58] select-none">
                <span>Show totals in</span>
                <select
                  value={displayCurrency}
                  onChange={(e) => setDisplayCurrency(e.target.value)}
                  className="border border-[#E8E8E6] rounded px-1.5 py-0.5 text-xs bg-white cursor-pointer focus:ring-1 focus:ring-[#1B4332] focus:outline-none"
                  data-testid="bookings-display-currency"
                >
                  <option value="KES">KES only</option>
                  <option value="USD">USD only</option>
                  <option value="BOTH">Both</option>
                </select>
              </label>
              <label className="flex items-center gap-1.5 text-xs font-normal text-[#525C58] cursor-pointer select-none" data-testid="hide-cancelled-toggle-label">
                <input
                  type="checkbox"
                  checked={hideCancelled}
                  onChange={(e) => setHideCancelled(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-[#7A8A84] text-[#1B4332] focus:ring-[#1B4332] cursor-pointer"
                  data-testid="hide-cancelled-toggle"
                />
                Hide cancelled
                {cancelledCount > 0 && (
                  <span className="text-[10px] text-[#7A8A84]">({cancelledCount})</span>
                )}
              </label>
              {pendingCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowOnlyPending((v) => !v)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                    showOnlyPending
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100"
                  }`}
                  data-testid="toggle-pending-housing-filter"
                >
                  <ClipboardCheck className="h-3.5 w-3.5" />
                  {pendingCount} need{pendingCount !== 1 ? "" : "s"} finalisation
                  {showOnlyPending && <X className="h-3 w-3 ml-1" />}
                </button>
              )}
              {canManage && untaggedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowOnlyUntagged((v) => !v)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                    showOnlyUntagged
                      ? "bg-[#1B4332] text-white border-[#1B4332]"
                      : "bg-[#F0F5F2] text-[#1B4332] border-[#1B4332]/20 hover:bg-[#E3EEE8]"
                  }`}
                  data-testid="toggle-untagged-filter"
                  title="Bookings with no sending agency assigned — open one and pick a sponsor to tag it"
                >
                  {untaggedCount} without agency
                  {showOnlyUntagged && <X className="h-3 w-3 ml-1" />}
                </button>
              )}
              {canManage && sponsorOrgs.length > 0 && (
                <button
                  type="button"
                  onClick={() => (bulkMode ? exitBulkMode() : setBulkMode(true))}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                    bulkMode
                      ? "bg-[#40916C] text-white border-[#40916C]"
                      : "bg-white text-[#1B4332] border-[#1B4332]/20 hover:bg-[#F0F5F2]"
                  }`}
                  data-testid="toggle-bulk-tag-mode"
                  title="Select multiple bookings and assign one sending agency to all of them"
                >
                  <Handshake className="h-3.5 w-3.5" />
                  Bulk tag agency
                  {bulkMode && <X className="h-3 w-3 ml-1" />}
                </button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {bulkMode && (
            <div className="mb-4 rounded-lg border border-[#1B4332]/20 bg-[#F0F5F2] px-4 py-3 flex flex-wrap items-center gap-3" data-testid="bulk-tag-bar">
              <span className="text-sm text-[#1B4332] font-medium">
                {selectedIds.length} selected
              </span>
              <Select value={bulkSponsor} onValueChange={setBulkSponsor}>
                <SelectTrigger className="w-56 h-8 bg-white" data-testid="bulk-sponsor-select">
                  <SelectValue placeholder="Choose sending agency…" />
                </SelectTrigger>
                <SelectContent>
                  {sponsorOrgs.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={applyBulk} disabled={bulkSaving || !bulkSponsor || selectedIds.length === 0}
                      className="bg-[#1B4332] hover:bg-[#2D6A4F] h-8" data-testid="bulk-apply-btn">
                {bulkSaving ? "Tagging…" : `Tag ${selectedIds.length} booking${selectedIds.length === 1 ? "" : "s"}`}
              </Button>
              <Button size="sm" variant="ghost" onClick={exitBulkMode} className="h-8" data-testid="bulk-cancel-btn">
                Cancel
              </Button>
              <span className="text-[11px] text-[#525C58]">Tip: combine with the “without agency” filter to select only untagged bookings.</span>
            </div>
          )}
          {pendingCount > 0 && !showOnlyPending && (
            <div
              className="mb-4 rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
              data-testid="pending-housing-banner"
            >
              <ClipboardCheck className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                <strong>{pendingCount}</strong> draft booking{pendingCount === 1 ? " was" : "s were"} auto-created from approved
                requests awaiting housing. Open one, assign a unit and rate, then save as
                <span className="px-1.5 py-0.5 mx-1 rounded bg-white border border-emerald-200 text-emerald-700 font-medium text-xs">Confirmed</span>
                to notify the visitor.
              </span>
            </div>
          )}
          {loading ? <div className="py-10 text-center text-[#7A8A84]">Loading…</div> : sortedBookings.length === 0 ? (
            <p className="text-[#7A8A84] text-center py-10">
              {showOnlyPending ? "No bookings pending housing finalisation." : "No bookings yet."}
            </p>
          ) : (
            <Table data-testid="bookings-table">
              <TableHeader>
                <TableRow>
                  {bulkMode && (
                    <TableHead className="w-8">
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll}
                             className="h-3.5 w-3.5 cursor-pointer" data-testid="bulk-select-all" />
                    </TableHead>
                  )}
                  <TableHead>Visitor</TableHead>
                  <TableHead>Stay</TableHead>
                  <TableHead>Units</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Revised</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedBookings.map(b => (
                  <TableRow
                    key={b.id}
                    data-testid={`booking-row-${b.id}`}
                    className={`${b.pending_housing_approval && b.status === "draft" ? "bg-amber-50/40 " : ""}${bulkMode && selectedIds.includes(b.id) ? "bg-[#F0F5F2]" : ""}`}
                  >
                    {bulkMode && (
                      <TableCell className="w-8">
                        <input type="checkbox" checked={selectedIds.includes(b.id)} onChange={() => toggleSelect(b.id)}
                               className="h-3.5 w-3.5 cursor-pointer" data-testid={`bulk-select-${b.id}`} />
                      </TableCell>
                    )}
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{b.visitor_name}</span>
                        {b.visitor_no && (
                          <Badge className="bg-[#1B4332]/10 text-[#1B4332] border-0 font-mono text-[9px]" title="Visitor ID">
                            {b.visitor_no}
                          </Badge>
                        )}
                        <button onClick={() => setCommentFor(b)} className="text-[#7A8A84] hover:text-[#1B4332] inline-flex items-center gap-0.5"
                                title="Visitor comments" data-testid={`booking-comments-${b.id}`}>
                          <MessageSquare className="h-3 w-3" />
                          {(b.comments || []).length > 0 && <span className="text-[9px]">{b.comments.length}</span>}
                        </button>
                        {b.sponsor_org_id && sponsorMap[b.sponsor_org_id] && (
                          <Badge
                            className="bg-[#F0F5F2] text-[#1B4332] border border-[#1B4332]/20 text-[10px] px-1.5 py-0.5 inline-flex items-center gap-1"
                            title={`Sending agency: ${sponsorMap[b.sponsor_org_id]}`}
                            data-testid={`agency-chip-${b.id}`}
                          >
                            <Handshake className="h-2.5 w-2.5" />
                            {sponsorMap[b.sponsor_org_id]}
                          </Badge>
                        )}
                        {b.pending_housing_approval && b.status === "draft" && (
                          <Badge
                            className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5"
                            data-testid={`pending-housing-badge-${b.id}`}
                          >
                            Pending housing
                          </Badge>
                        )}
                        {b.shared_room && (
                          <Badge
                            className="bg-purple-100 text-purple-900 border border-purple-300 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5"
                            title={b.override_reason || "Room sharing / double booking override"}
                            data-testid={`shared-room-badge-${b.id}`}
                          >
                            Shared
                          </Badge>
                        )}
                      </div>
                      <p className="text-[10px] text-[#7A8A84]">{b.party_size} pax</p>
                      {b.request_id && (
                        <Link
                          to={`/requests/${b.request_id}`}
                          className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#1B4332] hover:text-[#40916C] hover:underline"
                          data-testid={`booking-request-link-${b.id}`}
                        >
                          <ExternalLink className="h-3 w-3" /> View originating request
                        </Link>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#525C58]">{b.stay_from} → {b.stay_to}</TableCell>
                    <TableCell className="text-sm text-[#525C58]">
                      {b.segments.map(s => unitMap[s.unit_id]?.name || "?").join(", ") || "—"}
                      <p className="text-[10px] text-[#7A8A84]">{b.segments.length} segment{b.segments.length !== 1 ? "s" : ""}</p>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums" data-testid={`booking-total-${b.id}`}>
                      {displayCurrency === "BOTH" ? (
                        Object.entries(b.totals || {}).filter(([, v]) => v).length ? (
                          Object.entries(b.totals || {}).filter(([, v]) => v).map(([k, v]) => (
                            <div key={k}>{k} {v.toLocaleString()}</div>
                          ))
                        ) : <span className="text-[#7A8A84]">—</span>
                      ) : displayCurrency === "USD" ? (
                        <div>$ {(b.total_in_usd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      ) : (
                        <div>KES {(b.total_in_kes ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLOR[b.status]} border text-xs`}>{b.status.replace("_", " ")}</Badge>
                    </TableCell>
                    <TableCell data-testid={`revised-cell-${b.id}`}>
                      {b.last_revised_by_name ? (
                        <div className="text-xs">
                          <p className="text-[#525C58]">{b.last_revised_by_name}</p>
                          <p className="text-[10px] text-[#7A8A84]">{b.last_revised_at ? new Date(b.last_revised_at).toLocaleDateString() : ""}</p>
                          {b.pending_revision ? (
                            canManage ? (
                              <Button size="sm" onClick={() => acknowledgeRevision(b)} disabled={ackingId === b.id}
                                      className="mt-1 h-6 px-2 text-[10px] bg-amber-600 hover:bg-amber-700"
                                      data-testid={`ack-revision-${b.id}`}>
                                <BadgeCheck className="h-3 w-3 mr-1" /> Acknowledge
                              </Button>
                            ) : (
                              <Badge className="mt-1 bg-amber-100 text-amber-900 border border-amber-300 text-[9px] uppercase"
                                     data-testid={`pending-revision-badge-${b.id}`}>Awaiting ack</Badge>
                            )
                          ) : (b.revisions || []).length > 0 && (
                            <Badge className="mt-1 bg-emerald-50 text-emerald-800 border border-emerald-200 text-[9px] uppercase"
                                   data-testid={`acked-badge-${b.id}`}>
                              <BadgeCheck className="h-2.5 w-2.5 mr-0.5" /> Acked
                            </Badge>
                          )}
                        </div>
                      ) : <span className="text-[#B9C0BD] text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      {canManage && b.status !== "cancelled" && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setEditing({ ...b, segments: b.segments.map(s => ({ ...s })), add_ons: (b.add_ons || []).map(a => ({ ...a })) })} className="text-[#1B4332]" data-testid={`edit-booking-${b.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => generateInvoice(b)} className="text-emerald-700" data-testid={`invoice-booking-${b.id}`}>
                            <ReceiptText className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => cancelBooking(b)} className="text-red-600" data-testid={`cancel-booking-${b.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                      {canAgencyTransport && b.status !== "cancelled" && (
                        <Button size="sm" variant="ghost" onClick={() => setTransportEditing(b)}
                                className="text-[#1B4332]" title="Flight & transport details"
                                data-testid={`agency-transport-${b.id}`}>
                          <Plane className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Phase 69 — initiator/agency flight & transport dialog */}
      <AgencyTransportDialog booking={transportEditing} marginDays={agencyMargin}
                             onSaved={load} onClose={() => setTransportEditing(null)} />

      {/* Phase 73 — visitor comments */}
      <VisitorCommentsDialog bookingId={commentFor?.id} visitorName={commentFor?.visitor_name}
                             onClose={() => setCommentFor(null)} onSaved={load} />

      {/* Big multi-segment form */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeBookingDialog()}>
        <DialogContent className={`${showCalendar ? "max-w-[1400px] w-[96vw]" : "max-w-4xl"} max-h-[94vh] overflow-y-auto`}>
          <DialogHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <DialogTitle>{editing?.id ? "Edit booking" : "New booking"}</DialogTitle>
                <DialogDescription>
                  Add one or more stay segments. Different units, different dates, different rates — all in one booking.
                  <span className="block mt-1 text-[#1B4332]">Tip: large parties can occupy multiple units at the same time — just add a segment per unit with overlapping dates.</span>
                </DialogDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCalendar((v) => !v)}
                className="shrink-0 border-[#1B4332]/20 text-[#1B4332] hover:bg-[#E9F5DB]"
                data-testid="toggle-inline-calendar"
              >
                {showCalendar ? <EyeOff className="h-3.5 w-3.5 mr-1.5" /> : <LayoutPanelLeft className="h-3.5 w-3.5 mr-1.5" />}
                {showCalendar ? "Hide calendar" : "Show calendar"}
              </Button>
            </div>
          </DialogHeader>

          {editing && (
            <div className={showCalendar ? "grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-6" : ""}>
              <div className="space-y-5 min-w-0">
              {editing.pending_housing_approval && editing.status === "draft" && editing.request_id && (
                <div
                  className="rounded-lg border border-amber-300 bg-amber-50/70 p-3 text-sm text-amber-900 flex items-start gap-2"
                  data-testid="auto-draft-hint"
                >
                  <ClipboardCheck className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    This draft was auto-created from an approved visitor request. Pick a unit, confirm the dates,
                    choose a rate card, then set status to
                    <span className="mx-1 px-1.5 py-0.5 rounded bg-white border border-emerald-200 text-emerald-700 text-xs font-medium">Confirmed</span>
                    to auto-approve housing and email the visitor.
                  </span>
                </div>
              )}
              {/* Phase 73 — returning visitor quick-fill */}
              <div className="space-y-1 rounded-lg border border-[#E8E8E6] bg-[#FAFAF8] p-2.5">
                <Label className="text-xs">Returning visitor? Search by name or V-number to auto-fill</Label>
                <Input placeholder="Start typing… e.g. V-0012 or a name" value={visitorQuery}
                       onChange={(e) => searchVisitors(e.target.value)} data-testid="returning-visitor-search" />
                {visitorHits.length > 0 && (
                  <div className="rounded border border-[#E8E8E6] bg-white max-h-36 overflow-y-auto">
                    {visitorHits.map((v) => (
                      <button key={v.id} type="button" onClick={() => pickVisitor(v)}
                              className="w-full text-left px-2 py-1.5 text-xs hover:bg-[#F0F5F2] flex items-center gap-2"
                              data-testid={`visitor-hit-${v.id}`}>
                        <span className="font-mono text-[10px] text-[#1B4332]">{v.visitor_no}</span>
                        <span className="font-medium">{v.name}</span>
                        <span className="text-[#7A8A84]">{v.email}</span>
                        <span className="ml-auto text-[#7A8A84]">{v.booking_count} stay(s)</span>
                      </button>
                    ))}
                  </div>
                )}
                {editing.visitor_no && (
                  <p className="text-[10px] text-emerald-700" data-testid="linked-visitor-hint">
                    Linked to {editing.visitor_no}{editing._license_pulled ? " — previous medical license info pulled in" : ""}
                  </p>
                )}
              </div>
              {/* Visitor + dates */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Visitor name *</Label>
                  <Input value={editing.visitor_name} onChange={(e) => setEditing({ ...editing, visitor_name: e.target.value })} data-testid="booking-visitor-input" />
                </div>
                <div className="space-y-2">
                  <Label>Visitor email</Label>
                  <Input
                    type="email"
                    value={editing.visitor_email}
                    onChange={(e) => setEditing({ ...editing, visitor_email: e.target.value })}
                    data-testid="booking-email-input"
                    list="visitor-email-suggestions"
                    autoComplete="off"
                  />
                  <datalist id="visitor-email-suggestions">
                    {emailSuggestions.map((s) => (
                      <option key={s.email} value={s.email}>{s.label !== s.email ? `${s.label} · ${s.sources.join(", ")}` : s.sources.join(", ")}</option>
                    ))}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>Overall arrival</Label>
                  <Input type="date" value={editing.stay_from} onChange={(e) => setEditing({ ...editing, stay_from: e.target.value })} data-testid="booking-stay-from" />
                </div>
                <div className="space-y-2">
                  <Label>Overall departure</Label>
                  <Input type="date" value={editing.stay_to} onChange={(e) => setEditing({ ...editing, stay_to: e.target.value })} data-testid="booking-stay-to" />
                </div>
                <div className="space-y-2">
                  <Label>Party size</Label>
                  <Input type="number" min={1} value={editing.party_size} onChange={(e) => setEditing({ ...editing, party_size: Number(e.target.value) || 1 })} />
                </div>
                {Number(editing.party_size) === 1 && (
                  <div className="space-y-2">
                    <Label>Guest gender <span className="text-[#7A8A84] font-normal text-xs">(used for gender-restricted housing)</span></Label>
                    <Select value={editing.guest_gender || "unspecified"}
                            onValueChange={(v) => setEditing({ ...editing, guest_gender: v === "unspecified" ? "" : v })}>
                      <SelectTrigger data-testid="booking-gender-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unspecified">Not specified</SelectItem>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="checked_in">Checked in</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Phase 58 — sending sponsor (optional) */}
                <div className="space-y-2 col-span-2">
                  <Label>Sending organisation <span className="text-[#7A8A84] font-normal">(optional)</span></Label>
                  <Select value={editing.sponsor_org_id || "none"} onValueChange={(v) => setEditing({ ...editing, sponsor_org_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="booking-sponsor-select"><SelectValue placeholder="No sponsor selected" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {sponsorOrgs.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Segments */}
              <div className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label className="text-base font-semibold">Stay segments</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={suggestSegments}
                      className="border-amber-300 bg-amber-50/60 text-amber-900 hover:bg-amber-100"
                      data-testid="suggest-segments-btn"
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1" /> Suggest housing
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={addSegment} className="border-[#1B4332]/20" data-testid="add-segment-btn">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add segment
                    </Button>
                  </div>
                </div>
                {editing.segments.length === 0 && (
                  <p className="text-sm text-[#7A8A84] text-center py-6 border border-dashed border-[#E8E8E6] rounded-lg">
                    Click <span className="font-medium text-amber-900">Suggest housing</span> to auto-fill based on live inventory,
                    or <span className="font-medium text-[#1B4332]">Add segment</span> to pick a unit manually.
                  </p>
                )}
                {editing.segments.map((seg, idx) => {
                  const unit = unitMap[seg.unit_id];
                  const nights = Math.max(0, (new Date(seg.to_date) - new Date(seg.from_date)) / 86400000);
                  const subtotal = nights * Number(seg.rate_per_night || 0);
                  const overCapacity = unit && seg.occupancy > unit.capacity;
                  return (
                    <div key={idx} className="border border-[#E8E8E6] rounded-xl p-4 space-y-3 bg-[#F4F4F1]/30" data-testid={`segment-${idx}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-[#7A8A84] uppercase tracking-wider">Segment {idx + 1}</p>
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeSegment(idx)} className="text-red-600" data-testid={`remove-segment-${idx}`}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div className="col-span-2">
                          <Label className="text-xs">Unit</Label>
                          <Select value={seg.unit_id} onValueChange={(v) => updateSegment(idx, { unit_id: v })}>
                            <SelectTrigger data-testid={`seg-unit-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {units.filter(u => u.active).map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.name} (cap {u.capacity})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">From</Label>
                          <Input type="date" value={seg.from_date} onChange={(e) => updateSegment(idx, { from_date: e.target.value })} data-testid={`seg-from-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-xs">To (checkout)</Label>
                          <Input type="date" value={seg.to_date} onChange={(e) => updateSegment(idx, { to_date: e.target.value })} data-testid={`seg-to-${idx}`} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div>
                          <Label className="text-xs">Occupancy</Label>
                          <Input type="number" min={1} value={seg.occupancy} onChange={(e) => updateSegment(idx, { occupancy: Number(e.target.value) || 1 })} />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs">Rate card</Label>
                          <Select value={seg.rate_card_id || "_custom"} onValueChange={(v) => updateSegment(idx, { rate_card_id: v === "_custom" ? null : v })}>
                            <SelectTrigger data-testid={`seg-rate-card-${idx}`}><SelectValue placeholder="Pick rate" /></SelectTrigger>
                            <SelectContent>
                              {rateCards.filter(rc => rc.active).map(rc => (
                                <SelectItem key={rc.id} value={rc.id}>
                                  {rc.name} — {rc.currency} {Number(rc.amount).toLocaleString()}
                                </SelectItem>
                              ))}
                              <SelectItem value="_custom">Custom (override below)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Currency</Label>
                          <Select value={seg.currency} onValueChange={(v) => updateSegment(idx, { currency: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="KES">KES</SelectItem>
                              <SelectItem value="USD">USD</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Rate / night</Label>
                          <Input type="number" min={0} value={seg.rate_per_night} onChange={(e) => updateSegment(idx, { rate_per_night: Number(e.target.value) || 0 })} />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Helper</Label>
                        <Select value={seg.cleaner_id || "_none"} onValueChange={(v) => updateSegment(idx, { cleaner_id: v === "_none" ? null : v })}>
                          <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">None</SelectItem>
                            {cleaners.filter(c => c.active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input value={seg.notes || ""} onChange={(e) => updateSegment(idx, { notes: e.target.value })} placeholder="Notes (e.g. ground floor required)" />
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-[#7A8A84]">{nights} night{nights !== 1 ? "s" : ""}</span>
                        <span className="font-medium tabular-nums text-[#1B4332]">
                          {seg.currency} {subtotal.toLocaleString()}
                        </span>
                      </div>
                      {overCapacity && (
                        <p className="text-xs text-red-700 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Occupancy {seg.occupancy} exceeds unit capacity ({unit.capacity})
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Conflict / coverage feedback */}
              {conflict && conflict.blocking && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 space-y-2" data-testid="booking-conflict">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium">{conflict.detail}</p>
                      <p className="text-xs mt-1">Need to share this room (e.g. a couple or friend staying with another visitor)? Enable the override below and add a reason — both bookings will flag as shared in the audit log.</p>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-red-900 font-medium">
                    <input
                      type="checkbox"
                      checked={!!editing.allow_override}
                      onChange={(e) => setEditing({ ...editing, allow_override: e.target.checked })}
                      data-testid="allow-override-checkbox"
                      className="h-4 w-4"
                    />
                    Override & allow room sharing
                  </label>
                  {editing.allow_override && (
                    <Input
                      placeholder="Reason (optional, e.g. 'Married couple — sharing is fine')"
                      value={editing.override_reason || ""}
                      onChange={(e) => setEditing({ ...editing, override_reason: e.target.value })}
                      className="bg-white border-red-200"
                      data-testid="override-reason-input"
                    />
                  )}
                </div>
              )}
              {conflict && !conflict.blocking && (
                <div className="rounded-lg bg-purple-50 border border-purple-300 p-3 text-sm text-purple-900 space-y-2" data-testid="booking-conflict-override">
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 shrink-0 mt-0.5 text-purple-700" />
                    <div className="flex-1">
                      <p className="font-medium">Room-share override active.</p>
                      <p className="text-xs mt-1">{conflict.detail}</p>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-purple-900 text-xs">
                    <input
                      type="checkbox"
                      checked={!!editing.allow_override}
                      onChange={(e) => setEditing({ ...editing, allow_override: e.target.checked })}
                      className="h-3 w-3"
                    />
                    Keep override enabled
                  </label>
                  <Input
                    placeholder="Reason (optional)"
                    value={editing.override_reason || ""}
                    onChange={(e) => setEditing({ ...editing, override_reason: e.target.value })}
                    className="bg-white border-purple-200"
                    data-testid="override-reason-input"
                  />
                </div>
              )}
              {!conflict && coverage && coverage.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="booking-coverage-gap">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  {coverage.length} night{coverage.length !== 1 ? "s" : ""} not covered by any segment: {coverage.slice(0, 5).join(", ")}{coverage.length > 5 ? "…" : ""}
                </div>
              )}
              {!conflict && coverage && coverage.length === 0 && editing.segments.length > 0 && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="booking-coverage-ok">
                  <Check className="h-4 w-4" /> All nights covered, no conflicts
                </div>
              )}

              {/* Add-ons */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Add-ons</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addAddOn} className="border-[#1B4332]/20" data-testid="add-addon-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add
                  </Button>
                </div>
                {editing.add_ons.map((a, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Label className="text-xs">Label</Label>
                      <Input value={a.label} onChange={(e) => updateAddOn(idx, { label: e.target.value })} placeholder="Meals, transfer, etc." />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" min={0} value={a.quantity} onChange={(e) => updateAddOn(idx, { quantity: Number(e.target.value) })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Unit price</Label>
                      <Input type="number" min={0} value={a.unit_price} onChange={(e) => updateAddOn(idx, { unit_price: Number(e.target.value) })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Currency</Label>
                      <Select value={a.currency} onValueChange={(v) => updateAddOn(idx, { currency: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="KES">KES</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeAddOn(idx)} className="col-span-1 text-red-600">
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Medical license workflow */}
              <MedicalLicenseSection editing={editing} setEditing={setEditing} mlDefaults={mlDefaults} emailSuggestions={emailSuggestions} />

              {surveyOptions.length > 0 && (
                <div className="rounded-lg border border-[#E8E8E6] p-3 space-y-1.5" data-testid="booking-survey-picker">
                  <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">Include post-trip survey (optional)</p>
                  {surveyOptions.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer px-1 rounded hover:bg-[#F4F4F1]">
                      <input type="checkbox" checked={(editing.survey_ids || []).includes(s.id)}
                             onChange={() => setEditing({
                               ...editing,
                               survey_ids: (editing.survey_ids || []).includes(s.id)
                                 ? editing.survey_ids.filter((x) => x !== s.id)
                                 : [...(editing.survey_ids || []), s.id],
                             })}
                             data-testid={`booking-survey-${s.id}`} />
                      {s.name}
                    </label>
                  ))}
                  <p className="text-[10px] text-[#7A8A84]">Sent automatically once the stay ends, with reminders until opened or marked complete.</p>
                </div>
              )}

              {/* Phase 55 — Transport (airport pickup / drop-off) */}
              <div className="rounded-lg border border-[#E8E8E6] p-4 space-y-3" data-testid="transport-section">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editing.transport?.needed}
                    onChange={(e) => {
                      if (e.target.checked) updateTransport({ needed: true });
                      else setEditing((p) => ({ ...p, transport: { ...ensureTransport(p.transport), needed: false } }));
                    }}
                    className="mt-1 h-4 w-4 rounded border-[#7A8A84] text-[#1B4332] focus:ring-[#1B4332]"
                    data-testid="transport-needed-checkbox"
                  />
                  <div>
                    <span className="text-sm font-medium text-[#1C1F1E]">Transport needed (airport pickup / drop-off)</span>
                    <p className="text-xs text-[#7A8A84]">Adds this visitor to the driver Transport List. Inbound and outbound legs can be edited at any point during the visit.</p>
                  </div>
                </label>

                {editing.transport?.needed && (
                  <div className="space-y-4 pl-6 pt-1 border-l-2 border-sky-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-[#7A8A84]">Rate</Label>
                        <Input
                          type="number" min={0} step="0.01"
                          value={editing.transport.rate_amount || 0}
                          onChange={(e) => updateTransport({ rate_amount: parseFloat(e.target.value) || 0 })}
                          className="h-9"
                          data-testid="transport-rate-amount"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#7A8A84]">Currency</Label>
                        <Select
                          value={editing.transport.rate_currency || "KES"}
                          onValueChange={(v) => updateTransport({ rate_currency: v })}
                        >
                          <SelectTrigger className="h-9" data-testid="transport-rate-currency"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="KES">KES</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-[#7A8A84]">Contact phone</Label>
                        <Input
                          value={editing.transport.contact_phone || ""}
                          onChange={(e) => updateTransport({ contact_phone: e.target.value })}
                          placeholder="+254 …"
                          className="h-9"
                          data-testid="transport-contact-phone"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-[#7A8A84]">Pickup location</Label>
                        <Input
                          value={editing.transport.pickup_location || ""}
                          onChange={(e) => updateTransport({ pickup_location: e.target.value })}
                          placeholder="e.g. JKIA Terminal 1A"
                          className="h-9"
                          data-testid="transport-pickup-location"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#7A8A84]">Drop-off location</Label>
                        <Input
                          value={editing.transport.dropoff_location || ""}
                          onChange={(e) => updateTransport({ dropoff_location: e.target.value })}
                          placeholder="e.g. AIC Kapsowar Mission Hospital"
                          className="h-9"
                          data-testid="transport-dropoff-location"
                        />
                      </div>
                    </div>

                    {/* Inbound / Outbound legs */}
                    {[
                      { key: "inbound", label: "Inbound (arrival)", color: "border-emerald-200 bg-emerald-50/30" },
                      { key: "outbound", label: "Outbound (departure)", color: "border-amber-200 bg-amber-50/30" },
                    ].map((leg) => (
                      <div key={leg.key} className={`rounded-lg border p-3 space-y-2 ${leg.color}`} data-testid={`transport-${leg.key}-section`}>
                        <p className="text-xs uppercase tracking-wider font-semibold text-[#1B4332]">{leg.label}</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs text-[#7A8A84]">Date</Label>
                            <Input
                              type="date"
                              value={editing.transport[leg.key]?.date || ""}
                              onChange={(e) => updateTransportLeg(leg.key, { date: e.target.value })}
                              className="h-9"
                              data-testid={`transport-${leg.key}-date`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#7A8A84]">Time</Label>
                            <Input
                              type="time"
                              value={editing.transport[leg.key]?.time || ""}
                              onChange={(e) => updateTransportLeg(leg.key, { time: e.target.value })}
                              className="h-9"
                              data-testid={`transport-${leg.key}-time`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#7A8A84]">Flight number</Label>
                            <Input
                              value={editing.transport[leg.key]?.flight_number || ""}
                              onChange={(e) => updateTransportLeg(leg.key, { flight_number: e.target.value })}
                              placeholder="KQ100"
                              className="h-9"
                              data-testid={`transport-${leg.key}-flight`}
                            />
                          </div>
                        </div>
                        <Input
                          value={editing.transport[leg.key]?.notes || ""}
                          onChange={(e) => updateTransportLeg(leg.key, { notes: e.target.value })}
                          placeholder="Notes for the driver (e.g. 'will text on landing', 'large luggage')"
                          className="h-9"
                          data-testid={`transport-${leg.key}-notes`}
                        />
                      </div>
                    ))}

                    <div>
                      <Label className="text-xs text-[#7A8A84]">General transport notes</Label>
                      <Input
                        value={editing.transport.notes || ""}
                        onChange={(e) => updateTransport({ notes: e.target.value })}
                        placeholder="Visible on every leg of the trip"
                        className="h-9"
                        data-testid="transport-notes"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Phase 52 — Visitor Calendar sync (medical visitors only) */}
              <div className="rounded-lg border border-[#E8E8E6] p-4 space-y-3" data-testid="visitor-calendar-sync-section">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editing.add_to_visitor_calendar}
                    onChange={(e) => setEditing((p) => ({ ...p, add_to_visitor_calendar: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-[#7A8A84] text-[#1B4332] focus:ring-[#1B4332]"
                    data-testid="add-to-visitor-calendar-checkbox"
                  />
                  <div>
                    <span className="text-sm font-medium text-[#1C1F1E]">Add to Visitor Calendar (medical visitor)</span>
                    <p className="text-xs text-[#7A8A84]">Creates a fully-approved request entry so the visitor appears on the Visitor Calendar PDFs and ICS feed.</p>
                  </div>
                </label>

                {editing.add_to_visitor_calendar && (
                  <div className="space-y-3 pl-6 pt-1 border-l-2 border-emerald-200">
                    <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="dept-approval-confirm-toggle">
                      <input
                        type="checkbox"
                        checked={!!editing.all_dept_approvals_confirmed}
                        onChange={(e) => setEditing((p) => ({ ...p, all_dept_approvals_confirmed: e.target.checked }))}
                        className="mt-1 h-4 w-4 rounded border-[#7A8A84] text-[#1B4332] focus:ring-[#1B4332]"
                        data-testid="dept-approval-confirm-checkbox"
                      />
                      <span className="text-[#1C1F1E]">
                        I confirm this visitor has been approved by <strong>all required departments</strong>.
                        <span className="block text-xs text-[#7A8A84] mt-0.5">Required to save — this becomes part of the audit trail.</span>
                      </span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-[#7A8A84]">Specialty *</Label>
                        <Input
                          value={editing.visitor_specialty || ""}
                          onChange={(e) => setEditing((p) => ({ ...p, visitor_specialty: e.target.value }))}
                          placeholder="e.g. Pediatric Surgery"
                          className="h-9"
                          data-testid="visitor-specialty-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#7A8A84]">Department *</Label>
                        <Select
                          value={editing.visitor_department || ""}
                          onValueChange={(v) => setEditing((p) => ({ ...p, visitor_department: v }))}
                        >
                          <SelectTrigger className="h-9" data-testid="visitor-department-select">
                            <SelectValue placeholder="Select…" />
                          </SelectTrigger>
                          <SelectContent>
                            {departments.map((d) => (
                              <SelectItem key={d.id || d} value={d.key || d.name || d}>{d.name || d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-[#7A8A84]">Level *</Label>
                        <div className="flex gap-3 pt-1.5" data-testid="visitor-level-radio-group">
                          {[
                            { v: "student", label: "Student", color: "text-amber-700" },
                            { v: "resident", label: "Resident", color: "text-blue-700" },
                            { v: "faculty", label: "Faculty", color: "text-emerald-700" },
                          ].map((opt) => (
                            <label key={opt.v} className={`flex items-center gap-1.5 text-sm cursor-pointer ${opt.color}`}>
                              <input
                                type="radio"
                                name={`visitor-level-${editing.id || "new"}`}
                                checked={editing.visitor_level === opt.v}
                                onChange={() => setEditing((p) => ({ ...p, visitor_level: opt.v }))}
                                className="h-3.5 w-3.5 border-[#7A8A84] text-[#1B4332] focus:ring-[#1B4332]"
                                data-testid={`visitor-level-${opt.v}`}
                              />
                              <span>{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                    {editing.request_id && (
                      <p className="text-xs text-emerald-700 flex items-center gap-1.5" data-testid="visitor-calendar-linked-hint">
                        <Check className="h-3 w-3" /> Saving will update the linked Visitor Calendar entry.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Attachments (Phase 59) — only shown for existing bookings */}
              {editing.id && !String(editing.id).startsWith("new-") && (
                <div className="rounded-lg bg-white border border-[#E8E8E6] p-4">
                  <DocumentAttachments entityType="booking" entityId={editing.id} />
                </div>
              )}

              {/* Totals */}
              <div className="rounded-lg bg-[#1B4332]/5 border border-[#1B4332]/10 p-4" data-testid="booking-totals-block">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-wider text-[#7A8A84] font-semibold">Booking total</p>
                  <span className="text-[10px] text-[#7A8A84]">Stay + license + transport + add-ons</span>
                </div>
                <div className="space-y-1">
                  {Object.entries(totals).filter(([, v]) => v).map(([cur, v]) => (
                    <div key={cur} className="flex justify-between text-base font-medium">
                      <span>{cur}</span>
                      <span className="tabular-nums text-[#1B4332]">{v.toLocaleString()}</span>
                    </div>
                  ))}
                  {Object.values(totals).every(v => !v) && <p className="text-sm text-[#7A8A84]">No charges yet</p>}
                </div>
                {/* Phase 51.4 — Combined converted total when both currencies present */}
                {(Number(totals.KES || 0) > 0 && Number(totals.USD || 0) > 0) && (
                  <div className="mt-3 pt-3 border-t border-[#1B4332]/10 text-xs text-[#525C58]">
                    <div className="flex justify-between">
                      <span>Combined in KES (at {orgFxRate.toFixed(2)})</span>
                      <span className="tabular-nums font-semibold text-[#1B4332]">KES {convertedTotals.inKes.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Combined in USD</span>
                      <span className="tabular-nums font-semibold text-[#1B4332]">$ {convertedTotals.inUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Booking notes</Label>
                <Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
              </div>
              {showCalendar && (
                <div className="min-w-0">
                  <InlineCalendar
                    days={calendarDays}
                    grid={calendarGrid}
                    loading={calendarLoading}
                    editingBookingId={editing.id || null}
                    editingSegments={editing.segments}
                    stayFrom={editing.stay_from}
                    stayTo={editing.stay_to}
                    onClickUnit={(unitId) => {
                      // Handy: clicking a unit name adds a segment on that unit spanning the stay window.
                      const firstCard = rateCards.find((rc) => rc.active);
                      setEditing({
                        ...editing,
                        segments: [
                          ...editing.segments,
                          {
                            unit_id: unitId,
                            from_date: editing.stay_from,
                            to_date: editing.stay_to,
                            occupancy: editing.party_size || 1,
                            rate_card_id: firstCard?.id || null,
                            rate_card_name: firstCard?.name || "",
                            rate_per_night: firstCard?.amount || 0,
                            currency: firstCard?.currency || "KES",
                            cleaner_id: null,
                            notes: "",
                          },
                        ],
                      });
                    }}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {editing?.id && canManage && (
              <Button
                variant="outline"
                onClick={deleteBookingFromDialog}
                className="border-red-300 text-red-600 hover:bg-red-50 mr-auto"
                data-testid="delete-booking-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete booking
              </Button>
            )}
            <Button variant="outline" onClick={closeBookingDialog}>Cancel</Button>
            <Button onClick={saveBooking} disabled={!!(conflict && conflict.blocking)} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="save-booking-btn">
              <Check className="h-4 w-4 mr-2" /> Save booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 51 — Import-from-Excel dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { if (!o) { setImportFile(null); setImportPreview(null); setImportRows([]); } setImportOpen(o); }}>
        <DialogContent className="max-w-[1200px] w-[96vw] max-h-[92vh] overflow-y-auto" data-testid="import-excel-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: "Manrope, sans-serif" }}>Import bookings from Excel</DialogTitle>
            <DialogDescription>
              Upload a .xlsx workbook with monthly room-grid sheets (column&nbsp;A = unit, columns B+ = dates).
              Same name across consecutive cells = one stay. All imports land as <span className="font-medium text-gray-700">draft bookings</span> so you can review and finalise them later.
            </DialogDescription>
          </DialogHeader>

          {!importPreview && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border-2 border-dashed border-[#E8E8E6] p-6 text-center bg-[#FCFCFB]">
                <Upload className="h-8 w-8 mx-auto text-[#7A8A84] mb-2" />
                <input
                  type="file"
                  accept=".xlsx,.xlsm"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="block mx-auto text-sm text-[#525C58]"
                  data-testid="import-excel-file-input"
                />
                {importFile && (
                  <p className="text-xs text-[#525C58] mt-2"><span className="font-medium">{importFile.name}</span> · {(importFile.size / 1024).toFixed(1)} KB</p>
                )}
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                <p className="font-semibold mb-1">What to expect</p>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li>We scan the <strong>Master List</strong> (per-visitor rows) AND every <strong>monthly calendar sheet</strong>, then merge entries for the same visitor into one candidate.</li>
                  <li>When the master list and a monthly grid disagree (dates, unit), you&apos;ll see a <span className="text-amber-700">⚠ warnings</span> badge — review those rows carefully.</li>
                  <li>Historical &ldquo;Previous Visitors&rdquo; sheets, tabular Transport, and scratch sheets are skipped automatically.</li>
                  <li>Everything imports as <strong>draft — needs review</strong>. Confirm each booking in the list to promote it to Confirmed.</li>
                </ul>
              </div>
            </div>
          )}

          {importPreview && (
            <div className="space-y-3 py-2">
              <div className="flex items-center justify-between flex-wrap gap-2 text-sm">
                <div className="text-[#525C58]">
                  Found <span className="font-semibold text-[#1C1F1E]">{importPreview.total}</span> candidates ·{" "}
                  <span className="text-emerald-700">{importRows.filter((r) => r.unit_id).length} matched</span> ·{" "}
                  <span className="text-amber-700">{importRows.filter((r) => !r.unit_id).length} unmatched</span> ·{" "}
                  <span className="text-blue-700">{importRows.filter((r) => r.likely_duplicate).length} likely duplicates</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => toggleAllImportRows(true)} data-testid="import-select-all-btn">Select all matched</Button>
                  <Button size="sm" variant="outline" onClick={() => toggleAllImportRows(false)} data-testid="import-deselect-all-btn">Deselect all</Button>
                </div>
              </div>

              {importPreview.counts_by_source && (
                <div className="flex flex-wrap items-center gap-2 text-xs text-[#525C58]" data-testid="import-source-counts">
                  <Badge className="bg-purple-100 text-purple-900 border border-purple-200">🧩 Merged: {importPreview.counts_by_source.merged || 0}</Badge>
                  <Badge className="bg-emerald-100 text-emerald-900 border border-emerald-200">📅 Grid only: {importPreview.counts_by_source.grid_only || 0}</Badge>
                  <Badge className="bg-blue-100 text-blue-900 border border-blue-200">📋 Master only: {importPreview.counts_by_source.master_only || 0}</Badge>
                  <span className="text-[#7A8A84] italic ml-1">All imports land as draft with &ldquo;needs review&rdquo; until you confirm.</span>
                </div>
              )}

              {importPreview.counts_by_sheet && (
                <details className="rounded-lg border border-[#E8E8E6] bg-[#FAFAF8] p-2 text-xs" data-testid="import-per-sheet-breakdown">
                  <summary className="cursor-pointer text-[#525C58] font-medium">📋 Per-sheet breakdown ({Object.values(importPreview.counts_by_sheet).reduce((a, b) => a + b, 0)} rows total across {Object.keys(importPreview.counts_by_sheet).length} sheets)</summary>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 mt-2 text-[#525C58]">
                    {Object.entries(importPreview.counts_by_sheet).map(([sheet, count]) => (
                      <div key={sheet} className={`flex justify-between ${count === 0 ? 'text-[#9CA3AF]' : ''}`}>
                        <span className="truncate mr-2" title={sheet}>{sheet}</span>
                        <span className={`font-mono ${count === 0 ? 'text-[#9CA3AF]' : 'text-[#1B4332] font-semibold'}`}>{count}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {importPreview.skipped_sheets?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                  Skipped {importPreview.skipped_sheets.length} sheet(s): {importPreview.skipped_sheets.map((s) => s.sheet).join(", ")}
                </div>
              )}

              <div className="border rounded-lg overflow-x-auto max-h-[58vh]">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Visitor</TableHead>
                      <TableHead>Excel unit name</TableHead>
                      <TableHead>Map to unit</TableHead>
                      <TableHead>Arrival</TableHead>
                      <TableHead>Departure</TableHead>
                      <TableHead className="w-20">Party</TableHead>
                      <TableHead>Sheet</TableHead>
                      <TableHead>Flags</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importRows.map((r) => (
                      <TableRow
                        key={r._idx}
                        className={`${r.selected ? "bg-emerald-50/40" : ""} ${r.likely_duplicate ? "bg-blue-50/40" : ""}`}
                        data-testid={`import-row-${r._idx}`}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={!!r.selected}
                            disabled={!r.unit_id}
                            onChange={(e) => updateImportRow(r._idx, { selected: e.target.checked })}
                            className="h-4 w-4 rounded border-[#7A8A84] text-[#1B4332] focus:ring-[#1B4332]"
                            data-testid={`import-row-checkbox-${r._idx}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium text-[#1C1F1E]">{r.visitor_name}</TableCell>
                        <TableCell className="text-xs text-[#525C58] max-w-[200px] truncate" title={r.unit_name}>{r.unit_name}</TableCell>
                        <TableCell>
                          <Select value={r.unit_id || ""} onValueChange={(v) => updateImportRow(r._idx, { unit_id: v || null, selected: !!v && !r.likely_duplicate })}>
                            <SelectTrigger className="h-8 w-[200px] text-xs" data-testid={`import-row-unit-${r._idx}`}>
                              <SelectValue placeholder="Pick a unit…" />
                            </SelectTrigger>
                            <SelectContent>
                              {(importPreview.available_units || []).map((u) => (
                                <SelectItem key={u.id} value={u.id}>{u.name} (cap {u.capacity})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-xs">{r.from_date}</TableCell>
                        <TableCell className="text-xs">{r.to_date}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min={1}
                            value={r.party_size}
                            onChange={(e) => updateImportRow(r._idx, { party_size: parseInt(e.target.value) || 1 })}
                            className="h-8 w-16 text-xs"
                            data-testid={`import-row-party-${r._idx}`}
                          />
                        </TableCell>
                        <TableCell className="text-xs text-[#7A8A84]">{r.source_sheet}</TableCell>
                        <TableCell className="text-xs space-x-1">
                          {r.source === "merged" && <Badge className="bg-purple-100 text-purple-900 border border-purple-200 text-[10px]">🧩 merged</Badge>}
                          {r.source === "master" && <Badge className="bg-blue-100 text-blue-900 border border-blue-200 text-[10px]">📋 master</Badge>}
                          {(!r.source || r.source === "grid") && <Badge className="bg-emerald-100 text-emerald-900 border border-emerald-200 text-[10px]">📅 grid</Badge>}
                          {r.unit_match === "matched" ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]">matched</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px]">no auto-match</Badge>
                          )}
                          {r.likely_duplicate && (
                            <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-[10px]">duplicate?</Badge>
                          )}
                          {r.email && <Badge className="bg-gray-100 text-gray-700 border text-[10px]" title={r.email}>✉ email</Badge>}
                          {(r.warnings || []).length > 0 && (
                            <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px]" title={r.warnings.join(' · ')}>
                              ⚠ {r.warnings.length} warn
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {!importPreview ? (
              <>
                <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleImportPreview}
                  disabled={!importFile || importLoading}
                  className="bg-[#1B4332] hover:bg-[#2D6A4F]"
                  data-testid="import-preview-btn"
                >
                  {importLoading ? "Parsing…" : (<><Upload className="h-4 w-4 mr-2" /> Parse workbook</>)}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setImportPreview(null); setImportRows([]); }} data-testid="import-back-btn">
                  ← Choose another file
                </Button>
                <Button
                  onClick={handleImportCommit}
                  disabled={importCommitting || importRows.filter((r) => r.selected && r.unit_id).length === 0}
                  className="bg-[#1B4332] hover:bg-[#2D6A4F]"
                  data-testid="import-commit-btn"
                >
                  {importCommitting ? "Importing…" : `Create ${importRows.filter((r) => r.selected && r.unit_id).length} draft booking(s)`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const OTHER_STATUS_COLOR = {
  draft: "bg-gray-200 text-gray-700 border border-gray-300",
  confirmed: "bg-slate-200 text-slate-800 border border-slate-300",
  checked_in: "bg-blue-100 text-blue-800 border border-blue-300",
  completed: "bg-zinc-100 text-zinc-700 border border-zinc-300",
};

function InlineCalendar({ days, grid, loading, editingBookingId, editingSegments, stayFrom, stayTo, onClickUnit }) {
  // CRITICAL: use local-date formatting (not toISOString) so the day-key
  // matches the day-name shown in the header, otherwise users near midnight
  // UTC see Monday-headers next to Tuesday-coloured cells.
  const fmt = (d) => toLocalIso(d);
  const today = todayLocalIso();
  // Build a lookup: unitId -> Set of dates covered by current-editing segments
  const ownSegmentDates = {};
  for (const s of editingSegments || []) {
    if (!s.unit_id || !s.from_date || !s.to_date) continue;
    const cur = parseLocalDate(s.from_date);
    const end = parseLocalDate(s.to_date);
    while (cur < end) {
      const key = `${s.unit_id}::${fmt(cur)}`;
      ownSegmentDates[key] = true;
      cur.setDate(cur.getDate() + 1);
    }
  }

  if (!days.length) {
    return (
      <div className="rounded-lg border border-dashed border-[#E8E8E6] p-6 text-center text-sm text-[#7A8A84]">
        Set arrival and departure dates to preview the calendar.
      </div>
    );
  }

  return (
    <div className="sticky top-0">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-[#7A8A84] font-semibold">Live availability</p>
        <div className="flex items-center gap-3 text-[10px] text-[#7A8A84]">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> This booking</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-slate-300 inline-block" /> Other</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm border-2 border-[#1B4332]/40 inline-block" /> Stay window</span>
        </div>
      </div>
      <div className="rounded-lg border border-[#E8E8E6] bg-white overflow-hidden">
        <div className="overflow-x-auto max-h-[65vh]">
          {loading ? (
            <div className="py-10 text-center text-xs text-[#7A8A84]">Loading inventory…</div>
          ) : grid.units.length === 0 ? (
            <div className="py-10 text-center text-xs text-[#7A8A84]">No active housing units configured.</div>
          ) : (
            <table className="w-full border-collapse text-[11px]" data-testid="inline-calendar-grid">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#F4F4F1] border-b border-[#E8E8E6]">
                  <th className="sticky left-0 bg-[#F4F4F1] z-20 w-32 p-2 text-left font-semibold text-[#1C1F1E] border-r border-[#E8E8E6]">
                    Unit
                  </th>
                  {days.map((d) => {
                    const ds = fmt(d);
                    const inStay = ds >= stayFrom && ds < stayTo;
                    const isToday = ds === today;
                    return (
                      <th
                        key={ds}
                        className={`p-1.5 font-medium border-r border-[#E8E8E6] min-w-[32px] ${
                          isToday ? "bg-[#E9F5DB] text-[#1B4332]" : inStay ? "bg-[#1B4332]/5 text-[#1B4332]" : "text-[#7A8A84]"
                        }`}
                      >
                        <div className="text-[9px] uppercase">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                        <div className="text-xs">{d.getDate()}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {grid.units.map((u) => (
                  <tr key={u.id} className="border-b border-[#E8E8E6] hover:bg-[#F4F4F1]/40" data-testid={`inline-row-${u.id}`}>
                    <td className="sticky left-0 bg-white z-10 p-2 border-r border-[#E8E8E6]">
                      <button
                        type="button"
                        onClick={() => onClickUnit?.(u.id)}
                        className="text-left hover:text-[#1B4332] transition-colors w-full truncate"
                        title="Add a segment on this unit"
                      >
                        <p className="font-medium text-[#1C1F1E] truncate">{u.name}</p>
                        <p className="text-[9px] text-[#7A8A84]">cap {u.capacity}</p>
                      </button>
                    </td>
                    {days.map((d) => {
                      const ds = fmt(d);
                      const inStay = ds >= stayFrom && ds < stayTo;
                      const other = (grid.bookings_by_unit[u.id] || []).find(
                        (b) => ds >= b.from_date && ds < b.to_date && b.booking_id !== editingBookingId,
                      );
                      const isOwn = ownSegmentDates[`${u.id}::${ds}`];
                      return (
                        <td
                          key={ds}
                          className={`border-r border-[#E8E8E6] p-0 h-9 relative ${inStay ? "bg-[#1B4332]/5" : ""}`}
                        >
                          {isOwn && (
                            <div
                              className="absolute inset-0.5 rounded-sm bg-emerald-500 text-white text-[9px] font-semibold flex items-center justify-center"
                              title="This booking's segment"
                              data-testid={`inline-own-${u.id}-${ds}`}
                            >
                              ✓
                            </div>
                          )}
                          {!isOwn && other && (
                            <div
                              title={`${other.visitor_name} · ${other.from_date} → ${other.to_date}`}
                              className={`absolute inset-0.5 rounded-sm text-[9px] px-1 py-0.5 truncate ${OTHER_STATUS_COLOR[other.status] || OTHER_STATUS_COLOR.confirmed}`}
                              data-testid={`inline-other-${u.id}-${ds}`}
                            >
                              {other.visitor_name.split(" ")[0]}
                            </div>
                          )}
                          {inStay && !isOwn && !other && (
                            <div className="absolute inset-0 border border-dashed border-[#1B4332]/30 pointer-events-none" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <p className="text-[11px] text-[#7A8A84] mt-2">
        Click any unit name on the left to add a segment. Green cells are this booking; grey cells are other bookings.
      </p>
    </div>
  );
}

// Medical license workflow toggle inside the booking dialog. Uses the org's
// default fee as a pre-fill when the user first ticks the checkbox.
function MedicalLicenseSection({ editing, setEditing, mlDefaults, emailSuggestions = [] }) {
  const [recipientDraft, setRecipientDraft] = useState("");
  const recipients = editing.medical_license_recipients || [];
  const enabled = !!editing.needs_medical_license;

  const toggle = (on) => {
    setEditing({
      ...editing,
      needs_medical_license: on,
      // First time turning ON → seed status + fee + recipients from org defaults.
      medical_license_status: on ? (editing.medical_license_status || "requested") : null,
      medical_license_recipients: on && recipients.length === 0
        ? [...(mlDefaults.default_recipients || [])]
        : recipients,
      medical_license_fee_amount: on
        ? (editing.medical_license_fee_amount || mlDefaults.default_fee_amount || 0)
        : 0,
      medical_license_fee_currency: on
        ? (editing.medical_license_fee_currency || mlDefaults.default_fee_currency || "KES")
        : (editing.medical_license_fee_currency || "KES"),
    });
  };

  const addRecipient = () => {
    const v = (recipientDraft || "").trim();
    if (!v) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error("Enter a valid email"); return; }
    if (recipients.includes(v)) { setRecipientDraft(""); return; }
    setEditing({ ...editing, medical_license_recipients: [...recipients, v] });
    setRecipientDraft("");
  };
  const removeRecipient = (em) => setEditing({
    ...editing,
    medical_license_recipients: recipients.filter((r) => r !== em),
  });

  return (
    <div className="rounded-lg border border-[#E8E8E6] p-3 space-y-3" data-testid="medical-license-block">
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => toggle(e.target.checked)}
          className="mt-1"
          data-testid="needs-license-toggle"
        />
        <div>
          <p className="text-sm font-medium text-[#1C1F1E] flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-[#1B4332]" /> Visitor needs a medical license
          </p>
          <p className="text-[11px] text-[#7A8A84]">Tick if this clinician must obtain a Kenyan practising license. Recipients listed below are notified by email.</p>
        </div>
      </label>

      {enabled && (
        <div className="space-y-3 pl-6 pt-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Profession / specialty</Label>
              <Input
                value={editing.medical_license_profession || ""}
                onChange={(e) => setEditing({ ...editing, medical_license_profession: e.target.value })}
                placeholder="e.g. OB-GYN, Surgeon, Anaesthetist"
                data-testid="license-profession-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes for licensing team</Label>
              <Input
                value={editing.medical_license_notes || ""}
                onChange={(e) => setEditing({ ...editing, medical_license_notes: e.target.value })}
                placeholder="Anything coordinators need to know"
                data-testid="license-notes-input"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> Notify these recipients</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="license-recipient-chips">
              {recipients.map((em) => (
                <span key={em} className="inline-flex items-center gap-1 bg-[#F4F4F1] border border-[#E8E8E6] rounded-full px-2 py-0.5 text-xs">
                  {em}
                  <button type="button" onClick={() => removeRecipient(em)} className="text-red-600 hover:text-red-700" data-testid={`remove-recipient-${em}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {recipients.length === 0 && <span className="text-[11px] text-amber-700">No recipients yet — add at least one to fire the notification email.</span>}
            </div>
            <div className="flex gap-2">
              <Input
                value={recipientDraft}
                onChange={(e) => setRecipientDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                placeholder="email@example.com"
                className="flex-1"
                data-testid="license-recipient-input"
                list="license-recipient-suggestions"
                autoComplete="off"
              />
              <datalist id="license-recipient-suggestions">
                {emailSuggestions
                  .filter((s) => !recipients.includes(s.email))
                  .map((s) => (
                    <option key={s.email} value={s.email}>{s.label !== s.email ? `${s.label} · ${s.sources.join(", ")}` : s.sources.join(", ")}</option>
                  ))}
              </datalist>
              <Button type="button" size="sm" variant="outline" onClick={addRecipient} className="border-[#1B4332]/20" data-testid="add-recipient-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
            </div>
            {emailSuggestions.length > 0 && (
              <p className="text-[10px] text-[#7A8A84]">
                💡 {emailSuggestions.length} previously-used email{emailSuggestions.length === 1 ? "" : "s"} available — start typing to filter.
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">License fee (added to invoice)</Label>
              <Input
                type="number"
                min={0}
                value={editing.medical_license_fee_amount || 0}
                onChange={(e) => setEditing({ ...editing, medical_license_fee_amount: Number(e.target.value) })}
                data-testid="license-fee-input"
              />
              {mlDefaults.default_fee_amount > 0 && (
                <p className="text-[10px] text-[#7A8A84]">Org default: {mlDefaults.default_fee_currency} {Number(mlDefaults.default_fee_amount).toLocaleString()}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Currency</Label>
              <Select value={editing.medical_license_fee_currency || "KES"}
                      onValueChange={(v) => setEditing({ ...editing, medical_license_fee_currency: v })}>
                <SelectTrigger data-testid="license-fee-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="KES">KES</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

